/**
 * Presentation mode: the walkthrough on a TV.
 *
 * Two things make a page readable across a room. Real fullscreen, so browser
 * chrome is gone, and a larger root font size — every size in this app is in
 * rem, so scaling the root scales text, padding and gaps together rather than
 * just making the letters bigger inside a cramped layout.
 */

import { useCallback, useEffect, useState } from "react";

/** Root font sizes in px. 16 is the browser default. */
export const ZOOM_STEPS = [16, 18, 20, 24, 28, 32] as const;
const DEFAULT_ZOOM_INDEX = 2; // 20px

export interface Presentation {
  active: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
}

export function usePresentation(): Presentation {
  const [active, setActive] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);

  const enter = useCallback(() => {
    setActive(true);
    // Fullscreen needs a user gesture; if the browser declines we still present.
    void document.documentElement.requestFullscreen?.().catch(() => {});
  }, []);

  const exit = useCallback(() => {
    setActive(false);
    if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
  }, []);

  const toggle = useCallback(() => {
    if (active) exit();
    else enter();
  }, [active, enter, exit]);

  // Escape leaves fullscreen without telling React, so follow the browser.
  useEffect(() => {
    const onChange = () => {
      if (!document.fullscreenElement) setActive(false);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Scale the root so rem-based sizing grows as a whole.
  useEffect(() => {
    const root = document.documentElement;
    if (active) root.style.fontSize = `${ZOOM_STEPS[zoomIndex]}px`;
    else root.style.removeProperty("font-size");
    return () => {
      root.style.removeProperty("font-size");
    };
  }, [active, zoomIndex]);

  return {
    active,
    enter,
    exit,
    toggle,
    zoom: ZOOM_STEPS[zoomIndex]!,
    zoomIn: () => setZoomIndex((index) => Math.min(index + 1, ZOOM_STEPS.length - 1)),
    zoomOut: () => setZoomIndex((index) => Math.max(index - 1, 0)),
    canZoomIn: zoomIndex < ZOOM_STEPS.length - 1,
    canZoomOut: zoomIndex > 0,
  };
}

/** True when a keystroke belongs to whatever the user is typing in. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}
